import { Availability } from '../state/AppState';
import { Provider, Location, Insurance, AppointmentTime, State, Service, Reason } from './types';
import {
  transformToProviders,
  transformToLocations,
  transformToInsurances,
  transformToServices,
  transformToReasons,
  transformToStates,
  transformToAppointmentTimes,
  getPrimaryProvider
} from './availabilityDataService';

/**
 * Pure utility functions for API data transformation.
 * 
 * These functions have no side effects and can be safely used
 * in React components and custom hooks.
 */

/**
 * Get providers from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Provider objects
 */
export function getProvidersFromAvailability(availabilities: Availability[] | null): Provider[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  return transformToProviders(availabilities);
}

/**
 * Get locations from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Location objects
 */
export function getLocationsFromAvailability(availabilities: Availability[] | null): Location[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  return transformToLocations(availabilities);
}

/**
 * Get insurances from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Insurance objects
 */
export function getInsurancesFromAvailability(availabilities: Availability[] | null): Insurance[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  return transformToInsurances(availabilities);
}

/**
 * Get services from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Service objects
 */
export function getServicesFromAvailability(availabilities: Availability[] | null): Service[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  return transformToServices(availabilities);
}

/**
 * Get reasons from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Reason objects
 */
export function getReasonsFromAvailability(availabilities: Availability[] | null): Reason[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  return transformToReasons(availabilities);
}

/**
 * Get states from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of State objects
 */
export function getStatesFromAvailability(availabilities: Availability[] | null): State[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  return transformToStates(availabilities);
}

/**
 * Get appointment times from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of AppointmentTime objects
 */
export function getAppointmentTimesFromAvailability(availabilities: Availability[] | null): AppointmentTime[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  return transformToAppointmentTimes(availabilities);
}

/**
 * Get primary provider from availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Primary Provider object or null
 */
export function getPrimaryProviderFromAvailability(availabilities: Availability[] | null): Provider | null {
  if (!availabilities || availabilities.length === 0) {
    return null;
  }
  return getPrimaryProvider(availabilities);
}

/**
 * Check if availability data is loaded.
 * 
 * @param availabilities - Array of availability objects
 * @returns True if availability data is loaded
 */
export function hasAvailabilityData(availabilities: Availability[] | null): boolean {
  return availabilities !== null && availabilities.length > 0;
}

/**
 * Get data source information.
 * 
 * @param availabilities - Array of availability objects
 * @returns Data source information string
 */
export function getDataSourceInfo(availabilities: Availability[] | null): string {
  if (hasAvailabilityData(availabilities)) {
    return 'Real API Data';
  }
  return 'Static Data';
}

// Legacy functions for backward compatibility
export async function getServices(availabilities?: Availability[] | null): Promise<Service[]> {
  // Use availability data if provided, otherwise return static services
  if (availabilities && availabilities.length > 0) {
    return getServicesFromAvailability(availabilities);
  }
  
  return [];
}
